import { useEffect, useMemo, useRef, useState } from "react";

import AsyncSelect from "react-select/async";
import { BarSpinner } from "@components/spinner";
import PropTypes from "prop-types";
import ReactSelect from "react-select";
import StatusChip from "@components/ui-components/status-chip";
import classnames from "classnames";
import debounce from "lodash/debounce";
import useAxios from "@hooks/use-axios";

// The one select for the whole app, built on react-select in `unstyled` mode so
// every slot is painted with the same Tailwind tokens as the rest of the fields
// (REACT_STYLES, used by the older form selects, hardcodes hex that predates
// these tokens). Callers still speak in raw values, not react-select option
// objects — `value` is a value and `onChange` hands one back.
const SIZE_CLASS = {
    xs: "py-1 pl-2",
    sm: "py-1 pl-3",
    md: "py-1 pl-3"
};

// Heights go through `styles`, not a Tailwind class: even when `unstyled`,
// react-select emits `minHeight: 38px` on the control from emotion, which wins
// over a `min-h-*` utility. `styles` is applied on top of that base, so it is
// the only reliable way to size the control.
const SIZE_HEIGHT = { xs: 30, sm: 36, md: 42 };

// The menu sits a notch below the control's 14px and on tighter rows: the
// native dropdown it replaced rendered a compact OS list, and matching the
// control exactly made the menu read as oversized.
const MENU_TEXT_CLASS = {
    xs: "text-[10px]",
    sm: "text-xs",
    md: "text-xs"
};

// A chip on every row is noise, so it flags only the exception — an option whose
// record is not active. Mirrors the reference's `status !== "active"` guard.
const ACTIVE_STATUS = "active";

const OPTION_PAD_CLASS = {
    xs: "px-2.5 py-1",
    sm: "px-3 py-1.5",
    md: "px-3 py-1.5"
};

const WIDTH_CLASS = {
    xs: "min-w-[84px]",
    sm: "min-w-[180px]",
    md: "min-w-[180px]"
};

// Options are either { value, label, disabled } or a bare string/number. Bare
// items are rendered verbatim — never case-massaged, since they carry acronyms
// ("NEFT", "USA") that capitalising would mangle.
const toOption = (option) => {
    if (option !== null && typeof option === "object") {
        return { ...option, isDisabled: option.disabled || option.isDisabled };
    }
    return { value: option, label: String(option) };
};

const BaseSelect = (props) => {
    const {
        value,
        onChange,
        options = [],
        placeholder = "",
        label = "",
        error = "",
        invalid = false,
        size = "md",
        disabled = false,
        required = false,
        loading = false,
        isMulti = false,
        isSearchable = true,
        // A placeholder means the empty value is allowed, so the control is
        // clearable by default exactly where the old native markup offered an
        // empty first option.
        isClearable = Boolean(placeholder),
        // Pass `loadOptions` to fetch options remotely; the control then
        // behaves like react-select's AsyncSelect and `value` is the option
        // object, since a raw value has no label to resolve against.
        loadOptions = null,
        defaultOptions = false,
        helpText = "",
        name,
        id,
        className = "",
        selectClassName = "",
        "data-cy": dataCy,
        ...rest
    } = props;

    const inputId = id || name;
    const hasError = Boolean(error) || invalid;
    const menuTextClass = MENU_TEXT_CLASS[size] || MENU_TEXT_CLASS.md;
    const noticeClass = classnames("px-3 py-2 text-muted", menuTextClass);
    const selectOptions = options.map(toOption);

    // The menu is portalled so it escapes the Drawer/Modal overflow; both of
    // those sit at z-[200]-z-[210], so it has to clear them.
    // `cursor` sits here for the same reason as `minHeight`: react-select's base
    // control and option css set `cursor: default` even when `unstyled`, and
    // emotion is injected after the Tailwind sheet, so a `cursor-*` utility
    // loses. `styles` is layered on top of that base, so it wins.
    const styles = useMemo(() => ({
        menuPortal: (base) => ({ ...base, zIndex: 300 }),
        control   : (base) => ({
            ...base,
            minHeight: SIZE_HEIGHT[size] || SIZE_HEIGHT.md,
            cursor   : disabled ? "not-allowed" : "pointer"
        }),
        option: (base, state) => ({ ...base, cursor: state.isDisabled ? "not-allowed" : "pointer" }),
        // The menu sizes to its content rather than being pinned to the control's
        // width, so a narrow filter can still show a long brand name. Capped so a
        // single long label cannot stretch it across the viewport.
        menu  : (base) => ({ ...base, width: "max-content", minWidth: "100%", maxWidth: "min(24rem, 90vw)" })
    }), [size, disabled]);

    // One row: label truncated to a single line with the full text on hover, and
    // an optional StatusChip pushed right for options that carry a `status`.
    const formatOptionLabel = (option) => (
        <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate" title={typeof option.label === "string" ? option.label : undefined}>
                {option.label}
            </span>
            {option.status && option.status !== ACTIVE_STATUS && (
                <span className="ml-auto shrink-0">
                    <StatusChip tone={option.tone || "neutral"} label={option.status} />
                </span>
            )}
        </div>
    );

    // Held in a ref so the debounce survives re-renders — callers build
    // `loadOptions` inline, and debouncing a fresh function every render only
    // delays each keystroke instead of coalescing them.
    const loadOptionsRef = useRef(loadOptions);
    loadOptionsRef.current = loadOptions;
    const debouncedLoadOptions = useMemo(
        () => debounce((input, callback) => loadOptionsRef.current?.(input, callback), 400),
        []
    );

    const findOption = (val) => selectOptions.find((option) => option.value === val) || null;
    const resolveValue = () => {
        if (isMulti) return selectOptions.filter((option) => (value || []).includes(option.value));
        // Async callers keep the whole option around; there is nothing local to look it up in.
        if (value !== null && typeof value === "object") return value;
        return findOption(value);
    };

    const handleChange = (option) => {
        if (isMulti) {
            onChange?.((option || []).map((item) => item.value), option);
            return;
        }
        onChange?.(option ? option.value : "", option);
    };

    const SelectComponent = loadOptions ? AsyncSelect : ReactSelect;

    return (
        <div
            data-cy={dataCy || (inputId ? `${inputId}-select` : undefined)}
            className={classnames("inline-block", WIDTH_CLASS[size] || WIDTH_CLASS.md, { [className]: className })}>
            {label && (
                <label htmlFor={inputId} className="mb-1.5 block text-sm font-medium text-gray-700">
                    {label}
                    {required && <span className="font-bold text-danger">*</span>}
                </label>
            )}
            <div className={classnames({ "cursor-not-allowed": disabled })}>
                <SelectComponent
                    unstyled
                    inputId={inputId}
                    name={name}
                    value={resolveValue()}
                    options={loadOptions ? undefined : selectOptions}
                    loadOptions={loadOptions ? debouncedLoadOptions : undefined}
                    defaultOptions={loadOptions ? defaultOptions : undefined}
                    isMulti={isMulti}
                    isLoading={loading}
                    isDisabled={disabled}
                    isClearable={isClearable}
                    isSearchable={isSearchable}
                    placeholder={placeholder || "Select"}
                    onChange={handleChange}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={styles}
                    formatOptionLabel={formatOptionLabel}
                    loadingMessage={() => <BarSpinner className="py-2" />}
                    noOptionsMessage={() => "No matching options."}
                    classNames={{
                        // Every state gets one exclusive branch. Emitting e.g. both
                        // `bg-white` and `bg-gray-50` leaves the winner up to
                        // stylesheet order, which is why disabled used to look live.
                        control: ({ isFocused, isDisabled }) => classnames(
                            "rounded-lg border pr-2 text-sm",
                            SIZE_CLASS[size] || SIZE_CLASS.md,
                            isDisabled
                                ? "border-gray-200 bg-gray-50 text-gray-500"
                                : classnames(
                                    "bg-white text-gray-700",
                                    hasError ? "border-danger" : isFocused ? "border-primary" : "border-gray-300",
                                    isFocused && (hasError ? "ring-2 ring-danger/20" : "ring-2 ring-primary/20")
                                ),
                            selectClassName
                        ),
                        valueContainer    : () => "gap-1",
                        // gray-500 (#adb5bd), not gray-400 — in this theme gray-400
                        // is #636363, all but indistinguishable from body text.
                        placeholder       : ({ isDisabled }) => (isDisabled ? "text-gray-500" : "text-muted"),
                        singleValue       : ({ isDisabled }) => (isDisabled ? "text-gray-500" : "text-gray-700"),
                        input             : () => "text-gray-700",
                        indicatorSeparator: () => "hidden",
                        dropdownIndicator : ({ isDisabled }) => classnames("pl-1", isDisabled ? "text-gray-500" : "text-muted"),
                        clearIndicator    : () => "cursor-pointer px-1 text-muted hover:text-gray-700",
                        loadingIndicator  : () => "pr-1 text-muted",
                        multiValue        : ({ isDisabled }) => classnames(
                            "rounded bg-light px-1.5 py-0.5 text-xs",
                            isDisabled ? "text-gray-500" : "text-gray-700"
                        ),
                        multiValueRemove: () => "ml-1 cursor-pointer text-muted hover:text-danger",
                        menu            : () => "mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg",
                        menuList        : () => "max-h-60 overflow-y-auto py-1",
                        loadingMessage  : () => noticeClass,
                        noOptionsMessage: () => noticeClass,
                        option          : ({ isSelected, isFocused, isDisabled }) => classnames(
                            OPTION_PAD_CLASS[size] || OPTION_PAD_CLASS.md,
                            menuTextClass,
                            isDisabled
                                ? "text-gray-500"
                                : isSelected
                                    ? "bg-primary/10 font-medium text-primary"
                                    : isFocused
                                        ? "bg-gray-50 text-gray-700"
                                        : "text-gray-700"
                        )
                    }}
                        {...rest} />
            </div>
            {helpText && <small className="mt-1 block text-muted" data-cy={`help-${inputId}`}>{helpText}</small>}
            {error && <small className="mt-1 block text-danger" data-cy={`error-${inputId}`}>{error}</small>}
        </div>
    );
};

BaseSelect.propTypes = {
    value   : PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array, PropTypes.object]),
    onChange: PropTypes.func,
    options : PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({
            value   : PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label   : PropTypes.node,
            disabled: PropTypes.bool,
            // Optional chip on the right of the row, e.g. flagging an inactive record.
            status  : PropTypes.string,
            tone    : PropTypes.string
        })
    ])),
    placeholder    : PropTypes.string,
    label          : PropTypes.node,
    // Muted hint under the control, for the rule a label cannot carry on its own.
    helpText       : PropTypes.node,
    error          : PropTypes.string,
    // Border-only error state, for callers that render their own message.
    invalid        : PropTypes.bool,
    size           : PropTypes.oneOf(["xs", "sm", "md"]),
    disabled       : PropTypes.bool,
    required       : PropTypes.bool,
    loading        : PropTypes.bool,
    isMulti        : PropTypes.bool,
    isSearchable   : PropTypes.bool,
    isClearable    : PropTypes.bool,
    loadOptions    : PropTypes.func,
    defaultOptions : PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
    name           : PropTypes.string,
    id             : PropTypes.string,
    // Layout for the wrapper (width, flex behaviour); `selectClassName` tweaks the control itself.
    className      : PropTypes.string,
    selectClassName: PropTypes.string
};

// Fetches its own options from an endpoint, so a feature does not need a
// bespoke options hook just to fill a dropdown. Kept as a separate component
// rather than a branch inside BaseSelect so the dozen static selects in the app
// never build an axios instance they have no use for.
const UrlSelect = (props) => {
    const {
        URL,
        queryParams = {},
        selectId = "_id",
        selectLabel = "name",
        searchParam = "search",
        value,
        onChange,
        ...rest
    } = props;

    // A dropdown that cannot load its list falls back to "No matching options."
    // on its own; a toast on top of that is just noise.
    // Always the API root: endpoint constants carry their own role segment, so
    // there is no second prop to keep in sync with the path.
    const { axiosInstance } = useAxios({ base: "base", showAlert: false });
    const [options, setOptions] = useState([]);
    const [fetching, setFetching] = useState(false);
    // The chosen option is remembered because a server-side search replaces the
    // list, and the selected value would otherwise lose the label it renders by.
    const [picked, setPicked] = useState(null);

    const paramsKey = JSON.stringify(queryParams);

    const fetchOptions = async (search) => {
        setFetching(true);
        try {
            const res = await axiosInstance.get(URL, { params: { ...queryParams, [searchParam]: search } });
            // Endpoints are not uniform, so unwrap defensively. The array check
            // matters: useAxios already returns response.data, so a `{ data: [...] }`
            // payload unwraps to the array itself, and reaching for `.data` on it
            // again would silently yield an empty list.
            const payload = res?.data ?? res;
            const items = Array.isArray(payload)
                ? payload
                : payload?.data || payload?.items || payload?.results || [];
            setOptions(items.map((item) => ({
                value : item[selectId],
                label : item[selectLabel],
                status: item.status
            })));
        } catch {
            setOptions([]);
        } finally {
            setFetching(false);
        }
    };

    const fetchRef = useRef(fetchOptions);
    fetchRef.current = fetchOptions;
    const debouncedSearch = useMemo(() => debounce((search) => fetchRef.current(search), 400), []);

    useEffect(() => {
        fetchRef.current("");
        return () => debouncedSearch.cancel();
    }, [URL, paramsKey]);

    const handleInputChange = (input, meta) => {
        if (meta.action === "input-change") debouncedSearch(input);
    };

    const handleChange = (nextValue, option) => {
        setPicked(option || null);
        onChange?.(nextValue, option);
    };

    const merged = picked && !options.some((option) => option.value === picked.value)
        ? [picked, ...options]
        : options;

    return (
        <BaseSelect
            {...rest}
            value={value}
            options={merged}
            loading={fetching}
            onChange={handleChange}
            onInputChange={handleInputChange}
            filterOption={null} />
    );
};

const Select = (props) => (props.URL ? <UrlSelect {...props} /> : <BaseSelect {...props} />);

Select.propTypes = {
    ...BaseSelect.propTypes,
    // Endpoint to pull options from — pass a constant from @constants/endpoints,
    // not a literal. Options arrive as { value: item[selectId], label: item[selectLabel] }
    // and `value` stays a raw value.
    URL        : PropTypes.string,
    queryParams: PropTypes.object,
    selectId   : PropTypes.string,
    selectLabel: PropTypes.string,
    // Query key the typed text is sent under while searching.
    searchParam: PropTypes.string
};

UrlSelect.propTypes = Select.propTypes;

export default Select;
